import { resolvedTicketByID, updateTicket } from '@/modules/Ticket/services'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import TicketRemedialMeasures from '@/modules/Ticket/components/Forms/TicketRemedialMeasures'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { yupResolver } from '@hookform/resolvers/yup'
import { DEFAULT_REMEDIAL, resolvedSchema } from './schema'
import { CheckCircle } from 'lucide-react'
import { useEffect } from 'react'
import HelpIcons from '@/icons/HelpIcons'
import { useAppStore } from '@/stores/useAppStore'
import contents from '@/modules/Ticket/help/ticket-resolved-help.md'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import TicketTimeRelease from '@/modules/Ticket/components/Forms/TicketTimeRelease'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface TicketResolvedNormalProps {
    ticket: any
}

const ResolvedNormalTicket: React.FC<TicketResolvedNormalProps> = ({
    ticket,
}) => {
    const { t } = useTranslation()
    const { setIsShowHelp, setHelpContent, isShowHelp } = useAppStore()
    const queryClient = useQueryClient()
    const { refetchAll: refetchLogs } = useDataGridRefetch({
        reactQueryKey: `ticket-logs-${ticket?.ticket_id}`,
    })
    const { control, handleSubmit, reset } = useForm({
        defaultValues: {},
        // @ts-ignore
        resolver: yupResolver(resolvedSchema(t)),
    })

    useEffect(() => {
        if (ticket) {
            reset({
                ticket_remedial_measures: {
                    text:
                        ticket.ticket_remedial_measures ||
                        DEFAULT_REMEDIAL ||
                        '',
                    attachments: [],
                },
                ticket_time_release: ticket.ticket_time_release || dayjs(),
            })
        }
    }, [ticket, reset])

    const { mutateAsync: updateTicketMutation } = useMutationWithHandler(
        { mutationFn: updateTicket },
        {
            reactQueryKeys: QUERY_KEYS.TICKETS,
            showSuccessToast: false,
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [
                        QUERY_KEYS.TICKET_DETAIL,
                        String(ticket.ticket_id),
                    ],
                })
                refetchLogs()
            },
        }
    )

    const { mutateAsync: resolvedTicketMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: resolvedTicketByID },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('updated-success'),
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [
                            QUERY_KEYS.TICKET_DETAIL,
                            String(ticket.ticket_id),
                        ],
                    })
                    refetchLogs()
                },
            }
        )

    const onSubmit = async (values: any) => {
        await updateTicketMutation({
            payload: {
                note: {
                    ...values,
                    ticket_id: ticket.ticket_id,
                    ticket_status: ticket?.ticket_status,
                    ticket_priority: ticket?.ticket_priority,
                    ticket_remedial_measures:
                        values?.ticket_remedial_measures?.text || '',
                    ticket_time_release:
                        values.ticket_time_release ||
                        dayjs(values.ticket_time_release).format(
                            'YYYY-MM-DD HH:mm:ss'
                        ),
                },
            },
        })

        await resolvedTicketMutation({ ticket_id: ticket.ticket_id })
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
            {/* Remedial Measures */}
            <TicketRemedialMeasures control={control} ticket={ticket} />

            {/* Divider */}
            <div className="border-t border-gray-200 my-4" />

            {/* Time Release */}
            <TicketTimeRelease control={control} />

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
                <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    startIcon={<CheckCircle className="w-4 h-4" />}
                    endIcon={
                        <HelpIcons
                            onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setIsShowHelp(!isShowHelp)
                                setHelpContent(contents)
                            }}
                        />
                    }
                    loading={isPending}
                    disabled={isPending}
                    onClick={handleSubmit(onSubmit)}
                >
                    {t('ticket-mark-resolved')}
                </Button>
            </div>
        </form>
    )
}

export default ResolvedNormalTicket
